import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { AttachmentDto, ReplyPreviewDto } from "../contract/chat.events";
import type { ChatClientErrorCode } from "../lib/errors";

/**
 * A message waiting for its `message:send` ACK. Only light, non-secret data
 * is persisted — ids and text; never files, Blob/Base64 or signed URLs. A
 * photo message enters the outbox only after every attachment is `ready`, so
 * `attachmentIds` is all it needs to survive a reload.
 */
export interface OutboxEntry {
  clientMsgId: string;
  conversationId: string;
  body: string | null;
  replyToId: string | null;
  attachmentIds: string[];
  createdAt: string;
  status: "queued" | "sending" | "failed";
  errorCode: ChatClientErrorCode | null;
  attempts: number;
  nextAttemptAt: number;
}

/** Display-only extras for a pending bubble, kept in memory (never persisted). */
export interface PendingExtras {
  replyTo: ReplyPreviewDto | null;
  attachments: AttachmentDto[];
}

const extras = new Map<string, PendingExtras>();
export const pendingExtras = {
  get: (clientMsgId: string) => extras.get(clientMsgId),
  set: (clientMsgId: string, value: PendingExtras) => extras.set(clientMsgId, value),
  delete: (clientMsgId: string) => extras.delete(clientMsgId),
  clear: () => extras.clear(),
};

interface OutboxState {
  ownerId: number | null;
  entries: OutboxEntry[];
  enqueue: (entry: Omit<OutboxEntry, "status" | "errorCode" | "attempts" | "nextAttemptAt">) => void;
  markSending: (clientMsgId: string) => void;
  scheduleRetry: (clientMsgId: string, errorCode: ChatClientErrorCode, delayMs: number) => void;
  markFailed: (clientMsgId: string, errorCode: ChatClientErrorCode) => void;
  /** User tapped «Повторить» — same clientMsgId (CONTRACT §5.3). */
  retry: (clientMsgId: string) => void;
  remove: (clientMsgId: string) => void;
  /** After a reload nothing is in flight any more. */
  requeueInFlight: () => void;
  claim: (userId: number) => void;
  clear: () => void;
}

const patch = (entries: OutboxEntry[], id: string, fn: (e: OutboxEntry) => OutboxEntry): OutboxEntry[] =>
  entries.map((e) => (e.clientMsgId === id ? fn(e) : e));

export const useChatOutbox = create<OutboxState>()(
  persist(
    (set) => ({
      ownerId: null,
      entries: [],
      enqueue: (entry) =>
        set((s) => ({
          entries: [...s.entries, { ...entry, status: "queued", errorCode: null, attempts: 0, nextAttemptAt: 0 }],
        })),
      markSending: (id) => set((s) => ({ entries: patch(s.entries, id, (e) => ({ ...e, status: "sending" })) })),
      scheduleRetry: (id, errorCode, delayMs) =>
        set((s) => ({
          entries: patch(s.entries, id, (e) => ({
            ...e,
            status: "queued",
            errorCode,
            attempts: e.attempts + 1,
            nextAttemptAt: Date.now() + delayMs,
          })),
        })),
      markFailed: (id, errorCode) =>
        set((s) => ({ entries: patch(s.entries, id, (e) => ({ ...e, status: "failed", errorCode })) })),
      retry: (id) =>
        set((s) => ({
          entries: patch(s.entries, id, (e) => ({ ...e, status: "queued", errorCode: null, attempts: 0, nextAttemptAt: 0 })),
        })),
      remove: (id) => {
        pendingExtras.delete(id);
        set((s) => ({ entries: s.entries.filter((e) => e.clientMsgId !== id) }));
      },
      requeueInFlight: () =>
        set((s) => ({ entries: s.entries.map((e) => (e.status === "sending" ? { ...e, status: "queued" } : e)) })),
      claim: (userId) =>
        set((s) => (s.ownerId === userId ? s : { ownerId: userId, entries: s.ownerId === null ? s.entries : [] })),
      clear: () => {
        pendingExtras.clear();
        set({ ownerId: null, entries: [] });
      },
    }),
    {
      name: "turaq-chat-outbox",
      version: 1,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ ownerId: s.ownerId, entries: s.entries }),
    },
  ),
);
